import { DtoBase } from "../Utils/DtoBase";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { MonitorType } from "../Core/MonitorType";
import { Guid } from "../Utils/Guid";
import { MonitorSettingsBase } from "../Monitors/MonitorSettings";
import { Schedule } from "../Utils/Schedule";
import { HashSet } from "../Utils/HashSet";
import { DeviceMonitorMap } from "./DeviceMonitorMap";
import { MonitorSettings_Ping } from "./MonitorSettings_Ping";
import { MonitorSettings_SnmpIf } from "./MonitorSettings_SnmpIf";
import { MonitorSettings_Config } from "./MonitorSettings_Config";
import { MonitorSettings_Snmp, SnmpMonitorEntry } from "./MonitorSettings_Snmp";
import { MonitorSettings_PRTG } from "./MonitorSettings_PRTG";
import { Device } from "../Core/Device";
import { MonitorEntry } from "../InfoSets/MonitorTable";
import { Dictionary, KeyValuePair, List, ObjUtils } from "../Utils";


export class MonitorPolicy extends DtoBase implements ObjectSerializer {

	public readonly DEFAULT_MAX_THREADS = 40;

	public get MonitorPolicyId(): Guid {
		if (this.HasGUID)
			return Guid.FromString(this.GUID);
		else
			return Guid.Empty;
	}
	public set MonitorPolicyId(value: Guid) {
		this.GUID = value.ToString();
	}
	public get HasMonitorPolicyId(): boolean {
		return this.HasGUID;
	}
	public VerifyMonitorPolicyId(): void {
		this.VerifyGuid();
	}

	public Name: string;
	public get HasName(): boolean {
		return !!this.Name;
	}

	public Enabled: boolean;

	// MonitorPolicy schedule (how often does this policy execute)
	public Schedule: Schedule;

	public LastPollTime: string;
	public get HasLastPollTime(): boolean {
		return !!this.LastPollTime;
	}

	public MonitorType: MonitorType;

	private _Settings: MonitorSettingsBase;
	public get Settings(): MonitorSettingsBase {
		return this._Settings;
	}
	public set Settings(value: MonitorSettingsBase) {
		if (!value)
			this.MonitorType = MonitorType.None;
		else
			this.MonitorType = value.MonitorType;

		this._Settings = value;
	}
	public get HasSettings(): boolean {
		return !!this._Settings;
	}

	// EVENTS configuration
	public EmailRecipients: string;
	public get HasEmailRecipients(): boolean {
		return !!this.EmailRecipients;
	}

	public EmailOnMonitorStates: string;
	public get HasEmailOnMonitorStates(): boolean {
		return !!this.EmailOnMonitorStates;
	}

	public EmailSystemRecipients: boolean;
	public EmailEvents: boolean;
	public EmailEventsAsText: boolean;
	public EmailSeparateEvents: boolean;
	public SlackEvents: boolean;
	public SlackWebhooks: string;
	public get HasSlackWebhooks(): boolean {
		return !!this.SlackWebhooks;
	}

	public LogEvents: boolean;
	public DesktopNotifyEvents: boolean;

	private _deviceGuids: HashSet<Guid>;
	public get DeviceGuids(): HashSet<Guid> {
		return this._deviceGuids;
	}

	public get HasDevices(): boolean {
		return (!!this.DeviceGuids && this.DeviceGuids.Count > 0);
	}

	private _deviceMonitorMap: DeviceMonitorMap;
	public get DeviceMonitorMap(): DeviceMonitorMap {
		return this._deviceMonitorMap;
	}

	private _removedDeviceGuids: HashSet<Guid>;
	public get RemovedDeviceGuids(): HashSet<Guid> {
		return this._removedDeviceGuids;
	}

	public IsNew: boolean;

	// Thread count used for this MonitorPolicy
	public MaxThreads: number;


	constructor() {
		super();
		this.Name = "";
		this.Enabled = true;
		this.Schedule = Schedule.CreateMinutesInterval(60);
		this.LastPollTime = "";
		this.EmailRecipients = "";
		this.EmailOnMonitorStates = "";
		this.EmailEvents = false;
		this.EmailEventsAsText = false;
		this.EmailSeparateEvents = false;
		this.SlackEvents = false;
		this.SlackWebhooks = "";
		this.LogEvents = false;
		this.DesktopNotifyEvents = false;
		this.EmailSystemRecipients = false;
		this.MonitorType = MonitorType.None;
		this.Settings = null;
		this._deviceGuids = new HashSet<Guid>();
		this._deviceMonitorMap = new DeviceMonitorMap(this);
		this._removedDeviceGuids = new HashSet<Guid>();
		this.IsNew = false;
		this.MaxThreads = this.DEFAULT_MAX_THREADS;
	}

	public get IsDeviceBased(): boolean {
		switch (this.MonitorType) {
			case MonitorType.Config:
			case MonitorType.CPU:
			case MonitorType.Disk:
			case MonitorType.Ping:
			case MonitorType.Port:
			case MonitorType.PRTG:
			case MonitorType.SNMP:
			case MonitorType.SNMP_Interface:
			case MonitorType.StalePort:
			case MonitorType.VHUB:
			case MonitorType.VSAT:
			case MonitorType.WMI:
				return true;
			case MonitorType.HTTP:
			case MonitorType.DNS:
			case MonitorType.DeviceTracker:
				return false;
			default:
				console.assert(false);
				return false;
		}
	}

	public static CreateMonitor(monitorType: MonitorType, enabled: boolean = true): MonitorPolicy {
		let newPolicy: MonitorPolicy = null;

		let policyName = "";

		switch (monitorType) {
			case MonitorType.Ping:
				policyName = "NEW PING Monitor";
				newPolicy = this.CreatePingMonitor(policyName, enabled);
				break;
			case MonitorType.SNMP_Interface:
				policyName = "NEW SNMP IF Monitor";
				newPolicy = this.CreateSnmpIfMonitor(policyName, enabled);
				break;
			case MonitorType.Config:
				policyName = "NEW Config Monitor";
				newPolicy = this.CreateConfigMonitor(policyName, enabled);
				break;
			case MonitorType.SNMP:
				policyName = "NEW SNMP Monitor";
				newPolicy = this.CreateSnmpMonitor(policyName, enabled);
				break;
			default:
				console.assert(false, "Unsupported monitor type: " + monitorType);
				break;
		}

		if (newPolicy != null) {
			// ENSURE that we have a NEW/VALID GUID for this MonitorPolicy
			newPolicy.VerifyMonitorPolicyId();

			if (newPolicy.HasSettings) {
				if (!newPolicy.Settings.HasMonitorPolicyId)
					newPolicy.Settings.MonitorPolicyId = newPolicy.MonitorPolicyId;
			}
		}

		return newPolicy;
	}

	public static CreatePingMonitor(policyName: string, enabled: boolean = true): MonitorPolicy {
		let newPolicy = new MonitorPolicy();

		newPolicy.Name = policyName;
		newPolicy.Enabled = enabled;
		newPolicy.Settings = new MonitorSettings_Ping();

		return newPolicy;
	}

	public static CreateSnmpIfMonitor(policyName: string, enabled: boolean = true): MonitorPolicy {
		let newPolicy = new MonitorPolicy();

		newPolicy.Name = policyName;
		newPolicy.Enabled = enabled;
		newPolicy.Settings = new MonitorSettings_SnmpIf();

		return newPolicy;
	}

	public static CreateConfigMonitor(policyName: string, enabled: boolean = true): MonitorPolicy {
		let newPolicy = new MonitorPolicy();

		newPolicy.Name = policyName;
		newPolicy.Enabled = enabled;
		newPolicy.Settings = new MonitorSettings_Config();

		return newPolicy;
	}

	public static CreateSnmpMonitor(policyName: string, enabled: boolean = true): MonitorPolicy {
		let newPolicy = new MonitorPolicy();

		newPolicy.Name = policyName;
		newPolicy.Enabled = enabled;
		newPolicy.Settings = new MonitorSettings_Snmp();

		return newPolicy;
	}

	public static CreatePrtgMonitor(policyName: string, enabled: boolean = true): MonitorPolicy {
		let newPolicy = new MonitorPolicy();

		newPolicy.Name = policyName;
		newPolicy.Enabled = enabled;
		newPolicy.Settings = new MonitorSettings_PRTG();

		return newPolicy;
	}

	public Clear(): void {
		this.ClearPersistenceProps();

		this.Name = "";
		this.Enabled = false;
		this.Schedule.Clear();
		this.LastPollTime = "";
		this.Settings = null;

		this._deviceGuids.Clear();
		this._deviceMonitorMap.Clear();
		this._removedDeviceGuids.Clear();
	}

	public Clone(): MonitorPolicy {
		let obj = {};
		this.Write(obj, {});

		let clone = new MonitorPolicy();
		clone.Read(obj, {});

		return clone;
	}

	public Copy(other: MonitorPolicy, copyCollectedValues: boolean = true): void {
		if (!other)
			return;

		super.CopyPersistenceProps(other);

		this.Name = other.Name;
		this.Enabled = other.Enabled;
		this.Schedule.Copy(other.Schedule);
		this.LastPollTime = other.LastPollTime;
		this.EmailRecipients = other.EmailRecipients;
		this.EmailOnMonitorStates = other.EmailOnMonitorStates;
		this.EmailEvents = other.EmailEvents;
		this.EmailEventsAsText = other.EmailEventsAsText;
		this.EmailSeparateEvents = other.EmailSeparateEvents;
		this.SlackEvents = other.SlackEvents;
		this.SlackWebhooks = other.SlackWebhooks;
		this.LogEvents = other.LogEvents;
		this.DesktopNotifyEvents = other.DesktopNotifyEvents;
		this.EmailSystemRecipients = other.EmailSystemRecipients;

		// COPY the DEVICE GUIDs bound to this POLICY
		this.DeviceGuids.Clear();
		other.DeviceGuids.ForEach((guid: Guid) => {
			this.DeviceGuids.Add(guid);
		}, this);

		// COPY the DeviceMonitorMap
		this._deviceMonitorMap.Clear();
		this._deviceMonitorMap.Copy(other._deviceMonitorMap, copyCollectedValues);

		// COPY the DEVICE-KEYs that have been un-bound from this POLICY
		this._removedDeviceGuids.Clear();
		other._removedDeviceGuids.ForEach((guid: Guid) => {
			this._removedDeviceGuids.Add(guid);
		}, this);

		if (other.HasSettings) {
			if (copyCollectedValues)
				this.Settings = other.Settings.Clone();
			else {
				if (this.HasSettings)
					this.Settings.CopyConfig(other.Settings);
				else
					this.Settings = other.Settings.CloneConfig();
			}
		}
	}

	public AddDevice(pollDevice: Device): boolean {
		console.assert(!!pollDevice);
		console.assert(pollDevice.HasGUID);
		if (!pollDevice)
			return false;
		if (!pollDevice.HasGUID)
			return false;

		let result: boolean = this.DeviceGuids.Add(pollDevice.GUID);
		if (result) {
			this._deviceMonitorMap.AddDevice(pollDevice);

			this._removedDeviceGuids.Remove(pollDevice.GUID);
		}

		return result;
	}

	public HasDevice(pollDevice: Device): boolean {
		if (!pollDevice)
			return false;
		if (!pollDevice.HasGUID)
			return false;

		if (this.DeviceGuids.Contains(pollDevice.GUID))
			return true;

		return false;
	}

	public RemoveDevice(pollDevice: Device): boolean {
		console.assert(pollDevice != null);
		console.assert(pollDevice.HasGUID);
		if (!pollDevice)
			return false;
		if (!pollDevice.HasGUID)
			return false;

		return this.RemoveDevice_Guid(pollDevice.GUID);
	}

	public RemoveDevice_Guid(deviceGuid: Guid): boolean {
		console.assert(!!deviceGuid);
		if (!deviceGuid)
			return false;

		let result: boolean = this.DeviceGuids.Remove(deviceGuid);

		// REMOVE the MonitorTable Map
		this._deviceMonitorMap.RemoveDevice_Guid(deviceGuid);

		this._removedDeviceGuids.Add(deviceGuid);

		return result;
	}

	public ClearDevices(): void {
		this.DeviceGuids.Clear();
		this._deviceMonitorMap.Clear();
		this._removedDeviceGuids.Clear();
	}

	public AddMonitor_Device(device: Device): MonitorEntry {
		console.assert(!!device);
		console.assert(device.HasGUID);
		if (!device)
			return null;

		let monitorEntry: MonitorEntry = this._deviceMonitorMap.AddMonitor(device);

		// Policy membership maintenance
		this.DeviceGuids.Add(device.GUID);
		this._removedDeviceGuids.Remove(device.GUID);

		return monitorEntry;
	}

	public AddMonitor_DeviceMonitorEntry(device: Device, monitorEntry: MonitorEntry): boolean {
		console.assert(!!device);
		console.assert(device.HasGUID);
		if (!device)
			return false;
		if (!device.HasGUID)
			return false;

		this.DeviceGuids.Add(device.GUID);
		this._deviceMonitorMap.Add(device.GUID, monitorEntry);
		this._removedDeviceGuids.Remove(device.GUID);

		return (this.DeviceGuids.Contains(device.GUID));
	}

	public AddUpdateMonitor(device: Device): MonitorEntry {
		console.assert(!!device);
		console.assert(device.HasGUID);
		if (!device)
			return null;

		let monitorEntry: MonitorEntry = this._deviceMonitorMap.AddUpdateMonitor(device);

		// Policy membership maintenance
		this.DeviceGuids.Add(device.GUID);
		this._removedDeviceGuids.Remove(device.GUID);

		return monitorEntry;
	}

	public GetMonitor(device: Device): MonitorEntry {
		console.assert(device.HasGUID);

		let monitorEntry: MonitorEntry = this._deviceMonitorMap.GetMonitor_Device(device);
		return monitorEntry;
	}

	public GetMonitor_DeviceGuid(deviceGuid: Guid): MonitorEntry {
		console.assert(!!deviceGuid);

		let monitorEntry: MonitorEntry = this._deviceMonitorMap.GetMonitor_DeviceGuid(deviceGuid);
		return monitorEntry;
	}

	public VerifyGuids(): boolean {
		let guidsAdded = false;

		// VERIFY the POLICY MonitorPolicyId / GUID
		if (!this.HasMonitorPolicyId) {
			this.VerifyMonitorPolicyId();
			guidsAdded = true;
		}

		if (this.IsDeviceBased) {
			if (this._deviceMonitorMap != null) {
				if (this._deviceMonitorMap.VerifyGuids())
					guidsAdded = true;
			}
		}

		if (this.HasSettings) {
			if (!this.Settings.HasMonitorPolicyId) {
				this.Settings.MonitorPolicyId = this.MonitorPolicyId;
				guidsAdded = true;
			}

			if (this.Settings.VerifyGuids())
				guidsAdded = true;
		}

		return guidsAdded;
	}

	public VerifyConfig(): string[] {

		let output = [];

		switch (this.MonitorType) {
			case MonitorType.SNMP:
				this.VerifyCustomSnmpConfig(output);
				break;
		}

		return output;
	}

	private VerifyCustomSnmpConfig(output: string[]): void {

		let monSettings = (this.Settings as MonitorSettings_Snmp);

		output.push("Verify Custom SNMP Monitor Config");

		if (!monSettings) {
			output.push("MonitorPolicy Settings empty");
		}
		else {
			ObjUtils.Compare(this.MonitorPolicyId, monSettings.MonitorPolicyId, "MonitorPolicy.MonitorPolicyId and Settings.MonitorPolicyId", output);
			ObjUtils.Compare(this.MonitorType, monSettings.MonitorType, "MonitorPolicy.MonitorType and Settings.MonitorType", output);
			ObjUtils.Compare(this, this.DeviceMonitorMap.MonitorPolicy, "MonitorPolicy.this and MonitorPolicy.DeviceMonitorMap.MonitorPolicy", output);
			ObjUtils.Compare(this.DeviceGuids, this.DeviceMonitorMap.DeviceGuids, "MonitorPolicy.DeviceGuids and MonitorPolicy.DeviceGuids", output);

			if (this.DeviceGuids.Equals(this.DeviceMonitorMap.DeviceGuids)) {

				let genericEntries: Dictionary<string, SnmpMonitorEntry> = monSettings.GetGenericEntries();
				let allDeviceEntries: Dictionary<string, SnmpMonitorEntry> = monSettings.GetDeviceEntries();

				let genericEntryGuids = new HashSet<Guid>();
				genericEntries.ForEach((pair: KeyValuePair<string, SnmpMonitorEntry>) => {
					genericEntryGuids.Add(pair.Value.Guid);
				});
				ObjUtils.Compare(genericEntries.Count, genericEntryGuids.Count, "genericEntries.Count and genericEntryGuids.Count", output);

				let allDeviceEntryGuids = new HashSet<Guid>();
				allDeviceEntries.ForEach((pair: KeyValuePair<string, SnmpMonitorEntry>) => {
					allDeviceEntryGuids.Add(pair.Value.Guid);
				});
				ObjUtils.Compare(allDeviceEntries.Count, allDeviceEntryGuids.Count, "allDeviceEntries.Count and allDeviceEntryGuids.Count", output);

				if (!ObjUtils.ListEquals(genericEntries.Keys(), allDeviceEntries.Keys())) {
					output.push("genericEntries.Keys differs from allDeviceEntries.Keys");
				}

				if (!ObjUtils.SetEquals(genericEntryGuids, allDeviceEntryGuids)) {
					output.push("genericEntryGuids differs from allDeviceEntryGuids");
				}

				genericEntries.ForEach((pair: KeyValuePair<string, SnmpMonitorEntry>) => {
					let genericEntry = pair.Value;
					if (allDeviceEntries.ContainsKey(genericEntry.Oid)) {
						let allDevicesEntry = allDeviceEntries.Item(genericEntry.Oid);
						ObjUtils.Compare(genericEntry.Guid, allDevicesEntry.Guid, "genericEntry.Guid and allDevicesEntry.Guid", output)
					}
					else {
						output.push("Generic entry OID is missing in allDeviceEntries: " + genericEntry.Oid);
					}
				});

				this.DeviceGuids.ForEach((deviceGuid: Guid) => {
					let deviceEntries = monSettings.GetEntriesForDevice(deviceGuid);

					let deviceEntryGuids = new HashSet<Guid>();
					deviceEntries.ForEach((pair: KeyValuePair<string, SnmpMonitorEntry>) => {
						deviceEntryGuids.Add(pair.Value.Guid);
					});

					if (!ObjUtils.ListEquals(genericEntries.Keys(), deviceEntries.Keys())) {
						output.push("genericEntries.Keys differs from deviceEntries.Keys");
					}

					if (!ObjUtils.SetEquals(genericEntryGuids, deviceEntryGuids)) {
						output.push("genericEntryGuids differs from deviceEntryGuids: " + deviceGuid);
					}

					genericEntries.ForEach((pair: KeyValuePair<string, SnmpMonitorEntry>) => {
						let genericEntry = pair.Value;
						if (deviceEntries.ContainsKey(genericEntry.Oid)) {
							let deviceEntry = deviceEntries.Item(genericEntry.Oid);
							ObjUtils.Compare(genericEntry.Guid, deviceEntry.Guid, "genericEntry.Guid and deviceEntry.Guid", output);
						}
						else {
							output.push("Generic entry OID is missing in deviceEntries: " + genericEntry.Oid);
						}
					});
				})

				this.DeviceGuids.ForEach((deviceGuid: Guid) => {
					let devMonitorEntry: MonitorEntry = this.GetMonitor_DeviceGuid(deviceGuid);

					ObjUtils.Compare(this.MonitorType, devMonitorEntry.MonitorType, "MonitorPolicy.MonitorType and DevMonitorEntry.MonitorType", output);
					ObjUtils.Compare(this.MonitorPolicyId, devMonitorEntry.MonitorPolicyId, "MonitorPolicy.MonitorPolicyId and DevMonitorEntry.MonitorPolicyId", output);

					let devMonitorSettings = devMonitorEntry.GetMonitorSettings_Snmp();
					ObjUtils.Compare(genericEntries.Count, devMonitorSettings.Count, "genericEntries.Count and devMonitorSettings.Count", output);

					devMonitorSettings.ForEach((devEntry: SnmpMonitorEntry) => {
						if (genericEntries.ContainsKey(devEntry.Oid)) {
							let genericEntry = genericEntries.Item(devEntry.Oid);
							ObjUtils.Compare(genericEntry.Guid, devEntry.Guid, "genericEntry.Guid and devEntry.Guid", output);
						}
						else {
							output.push("Device monitor entry does not exist in Generic monitor entries: " + devEntry.Oid);
						}
					});
				})
			}
			else {
				output.push("Skipping MonitorPolicy.DeviceMonitorMap verification because DeviceGuids do not match");
			}
		}
	}

	public Write(obj: any, context: any): void {
		obj.monitor_policy = {};
		obj = obj.monitor_policy;

		super.Write(obj, context);

		if (this.HasName)
			obj.name = this.Name;

		obj.enabled = ValueEncoder.EncodeBoolean(this.Enabled);

		this.Schedule.Write(obj, context);

		if (this.HasLastPollTime)
			obj.last_poll_time = this.LastPollTime;

		obj.monitor_type = MonitorType[this.MonitorType];

		if (this.HasSettings)
			this.Settings.Write(obj, context);

		if (this.HasEmailRecipients)
			obj.email_recipients = this.EmailRecipients;

		if (this.HasEmailOnMonitorStates)
			obj.email_on_monitor_states = this.EmailOnMonitorStates;

		obj.mail_events = ValueEncoder.EncodeBoolean(this.EmailEvents);
		obj.mail_events_as_txt = ValueEncoder.EncodeBoolean(this.EmailEventsAsText);
		obj.mail_events_seperate = ValueEncoder.EncodeBoolean(this.EmailSeparateEvents);
		obj.slack_events = ValueEncoder.EncodeBoolean(this.SlackEvents);
		if (this.HasSlackWebhooks)
			obj.webhooks = this.SlackWebhooks;
		obj.log_events = ValueEncoder.EncodeBoolean(this.LogEvents);
		obj.email_system_recipients = ValueEncoder.EncodeBoolean(this.EmailSystemRecipients);
		obj.desktop_notify_events = ValueEncoder.EncodeBoolean(this.DesktopNotifyEvents);

		if (this.HasDevices) {
			obj.device_guids = [];
			ObjSerUtils.WriteGuidCollection(obj.device_guids, this.DeviceGuids, "guid", context);
		}

		this._deviceMonitorMap.Write(obj, context);
	}

	public Read(obj: any, context: any): void {

		this.Clear();

		if (!obj.monitor_policy) {
			throw new Error();
		}
		obj = obj.monitor_policy;

		super.Read(obj, context);

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.enabled !== undefined)
			this.Enabled = ValueEncoder.DecodeBoolean(obj.enabled);

		if (obj.schedule !== undefined) {
			this.Schedule = new Schedule();
			this.Schedule.Read(obj, context);
		}

		if (obj.last_poll_time !== undefined)
			this.LastPollTime = obj.last_poll_time;

		if (obj.monitor_type !== undefined)
			this.MonitorType = MonitorType[<string>obj.monitor_type];

		if (obj.monitor_settings !== undefined) {
			this.Settings = MonitorSettingsBase.CreateInstance(this.MonitorType);
			if (this.Settings) {
				this.Settings.Read(obj, context);
			}
		}

		if (obj.email_recipients !== undefined)
			this.EmailRecipients = obj.email_recipients;

		if (obj.email_on_monitor_states !== undefined)
			this.EmailOnMonitorStates = obj.email_on_monitor_states;

		if (obj.mail_events !== undefined)
			this.EmailEvents = ValueEncoder.DecodeBoolean(obj.mail_events);

		if (obj.mail_events_as_txt !== undefined)
			this.EmailEventsAsText = ValueEncoder.DecodeBoolean(obj.mail_events_as_txt);

		if (obj.mail_events_seperate !== undefined)
			this.EmailSeparateEvents = ValueEncoder.DecodeBoolean(obj.mail_events_seperate);

		if (obj.slack_events !== undefined)
			this.SlackEvents = ValueEncoder.DecodeBoolean(obj.slack_events);

		if (obj.webhooks !== undefined)
			this.SlackWebhooks = obj.webhooks;

		if (obj.log_events !== undefined)
			this.LogEvents = ValueEncoder.DecodeBoolean(obj.log_events);

		if (obj.email_system_recipients !== undefined)
			this.EmailSystemRecipients = ValueEncoder.DecodeBoolean(obj.email_system_recipients);

		if (obj.desktop_notify_events !== undefined)
			this.DesktopNotifyEvents = ValueEncoder.DecodeBoolean(obj.desktop_notify_events);

		if (obj.device_guids !== undefined)
			ObjSerUtils.ReadGuidCollection(obj.device_guids, this.DeviceGuids, "guid", context);

		if (obj.device_monitor_map !== undefined)
			this.DeviceMonitorMap.Read(obj, context);
	}
}
